/*
  Shift modular sections · step calculator
  <sr-calculator>: daily-steps slider → distance on foot and in Dusk.
    on foot (mi)       = steps × stride (ft) ÷ 5,280
    in Dusk S / M (mi) = on foot × Small / Medium multiplier
    in Dusk L (mi)     = on foot × Large multiplier
    bar scale          = max steps × stride ÷ 5,280 × the larger multiplier
  Every constant comes from the section's block settings (data attributes).
  <sr-calc-map>: draws the body-map leader lines and places the mobile tags.
  "Find the right size" ([data-sr-calc-cta]) scrolls to the buy box.
  Custom elements, so they re-initialise on shopify:section:load by themselves.
*/
(function () {
  var FEET_PER_MILE = 5280;

  function compute(steps, c) {
    var foot = (steps * c.stride) / FEET_PER_MILE;
    var maxFoot = (c.max * c.stride) / FEET_PER_MILE;
    return {
      foot: foot,
      sm: foot * c.multSm,
      lg: foot * c.multL,
      scale: maxFoot * Math.max(c.multSm, c.multL)
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { compute: compute };
  }
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function num(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  function formatter(locale, options) {
    try {
      return new Intl.NumberFormat(locale || undefined, options);
    } catch (e) {
      return new Intl.NumberFormat(undefined, options);
    }
  }

  if (!customElements.get('sr-calculator')) {
    class SRCalculator extends HTMLElement {
      connectedCallback() {
        var d = this.dataset;
        this.config = {
          min: num(d.min, 1000),
          max: num(d.max, 15000),
          step: Math.max(1, num(d.step, 500)),
          stride: num(d.stride, 2.5),
          multSm: num(d.multSm, 2.14),
          multL: num(d.multL, 2.42)
        };
        if (this.config.max <= this.config.min) this.config.max = this.config.min + this.config.step;

        this.fmtSteps = formatter(d.locale, { maximumFractionDigits: 0 });
        this.fmtMiles = formatter(d.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        this.slider = this.querySelector('[data-slider]');
        this.input = this.querySelector('[data-input]');
        this.hit = this.querySelector('[data-hit]');
        this.bubble = this.querySelector('[data-bubble]');
        this.live = this.querySelector('[data-live]');
        this.ticks = Array.prototype.slice.call(this.querySelectorAll('[data-tick]'));
        this.out = {};
        Array.prototype.forEach.call(this.querySelectorAll('[data-out]'), (el) => {
          (this.out[el.dataset.out] = this.out[el.dataset.out] || []).push(el);
        });
        this.bars = {};
        Array.prototype.forEach.call(this.querySelectorAll('[data-bar]'), (el) => {
          this.bars[el.dataset.bar] = el;
        });
        if (!this.input || !this.slider) return;

        this.onInput = this.onInput.bind(this);
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.placeBubble = this.placeBubble.bind(this);

        this.input.addEventListener('input', this.onInput);
        if (this.hit) this.hit.addEventListener('pointerdown', this.onDown);
        this.resizeObserver = new ResizeObserver(this.placeBubble);
        this.resizeObserver.observe(this.slider);

        this.setText('min', this.fmtSteps.format(this.config.min) + ' ' + d.stepsUnit);
        this.setText('max', this.fmtSteps.format(this.config.max) + ' ' + d.stepsUnit);
        this.render(false);
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.input) this.input.removeEventListener('input', this.onInput);
        if (this.hit) this.hit.removeEventListener('pointerdown', this.onDown);
        window.clearTimeout(this.liveTimer);
      }

      value() {
        var c = this.config;
        var v = num(this.input.value, c.min);
        return Math.min(c.max, Math.max(c.min, v));
      }

      onInput() {
        this.render(true);
      }

      // Pointer: press anywhere on the track and drag; keyboard stays on the input
      onDown(e) {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        this.input.focus({ preventScroll: true });
        this.slider.classList.add('is-dragging');
        this.hit.setPointerCapture(e.pointerId);
        this.hit.addEventListener('pointermove', this.onMove);
        this.hit.addEventListener('pointerup', this.onUp);
        this.hit.addEventListener('pointercancel', this.onUp);
        this.setFromPointer(e);
      }

      onMove(e) {
        this.setFromPointer(e);
      }

      onUp(e) {
        this.slider.classList.remove('is-dragging');
        if (this.hit.hasPointerCapture(e.pointerId)) this.hit.releasePointerCapture(e.pointerId);
        this.hit.removeEventListener('pointermove', this.onMove);
        this.hit.removeEventListener('pointerup', this.onUp);
        this.hit.removeEventListener('pointercancel', this.onUp);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setFromPointer(e) {
        var c = this.config;
        var rect = this.slider.getBoundingClientRect();
        if (!rect.width) return;
        var t = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        var v = c.min + Math.round((t * (c.max - c.min)) / c.step) * c.step;
        v = Math.min(c.max, Math.max(c.min, v));
        if (String(v) !== this.input.value) {
          this.input.value = v;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      setText(key, text) {
        (this.out[key] || []).forEach(function (el) {
          el.textContent = text;
        });
      }

      render(announce) {
        var c = this.config;
        var d = this.dataset;
        var steps = this.value();
        var r = compute(steps, c);
        var p = ((steps - c.min) / (c.max - c.min)) * 100;
        var stepsText = this.fmtSteps.format(steps);
        var foot = this.fmtMiles.format(r.foot);
        var sm = this.fmtMiles.format(r.sm);
        var lg = this.fmtMiles.format(r.lg);
        var lo = Math.min(r.sm, r.lg);
        var hi = Math.max(r.sm, r.lg);
        var loText = this.fmtMiles.format(lo);
        var hiText = this.fmtMiles.format(hi);
        var dusk = loText === hiText ? loText : loText + (d.sep || '-') + hiText;
        var scale = function (v) {
          return 'scaleX(' + Math.min(1, Math.max(0, v / r.scale)).toFixed(4) + ')';
        };

        this.style.setProperty('--p', p.toFixed(3) + '%');
        this.input.setAttribute('aria-valuetext', stepsText + ' ' + d.stepsUnit);
        this.setText('steps', stepsText);
        this.setText('foot', foot);
        this.setText('dusk', dusk);
        this.setText('sm', sm);
        this.setText('lg', lg);

        if (this.bars.foot) this.bars.foot.style.transform = scale(r.foot);
        // Large drawn from 0 under the Small / Medium fill, so the two read as one bar
        if (this.bars.sm) this.bars.sm.style.transform = scale(lo);
        if (this.bars.lg) this.bars.lg.style.transform = scale(hi);
        this.ticks.forEach(function (tick) {
          tick.classList.toggle('is-on', num(tick.dataset.tick, Infinity) <= steps);
        });
        this.placeBubble();

        if (announce && this.live) {
          var about = d.about ? d.about + ' ' : '';
          var duskSpoken = loText === hiText ? loText : loText + ' ' + (d.to || 'to') + ' ' + hiText;
          var text =
            d.onFoot + ': ' + about + foot + ' ' + d.distUnit + '. ' +
            d.inDusk + ': ' + about + duskSpoken + ' ' + d.distUnit + '.';
          window.clearTimeout(this.liveTimer);
          this.liveTimer = window.setTimeout(() => {
            this.live.textContent = text;
          }, 500);
        }
      }

      // Keep the value bubble inside the slider; its pointer stays on the handle
      placeBubble() {
        if (!this.bubble) return;
        var width = this.slider.clientWidth;
        var bw = this.bubble.offsetWidth;
        var c = this.config;
        var x = ((this.value() - c.min) / (c.max - c.min)) * width;
        var nudge = 0;
        if (x - bw / 2 < 0) nudge = bw / 2 - x;
        else if (x + bw / 2 > width) nudge = width - bw / 2 - x;
        this.bubble.style.setProperty('--sr-calc-nudge', nudge.toFixed(1) + 'px');
      }
    }

    customElements.define('sr-calculator', SRCalculator);
  }

  if (!customElements.get('sr-calc-map')) {
    var SVG_NS = 'http://www.w3.org/2000/svg';
    var desktop = window.matchMedia('(min-width: 750px)');

    class SRCalcMap extends HTMLElement {
      connectedCallback() {
        this.schedule = this.schedule.bind(this);
        this.svg = this.querySelector('[data-leaders]');
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
        var img = this.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', this.schedule, { once: true });
        this.update();
        // Re-link footnote markers after a theme editor reload of this section
        if (window.SRFootnotes) window.SRFootnotes.run();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.cancelAnimationFrame(this.frame);
      }

      schedule() {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.update());
      }

      update() {
        if (!this.svg) return;
        var box = this.getBoundingClientRect();
        if (!box.width) return;
        var isDesktop = desktop.matches;
        var paths = [];

        Array.prototype.forEach.call(this.querySelectorAll('[data-hotspot]'), (spot) => {
          var id = spot.dataset.hotspot;
          var s = spot.getBoundingClientRect();
          var cx = s.left + s.width / 2 - box.left;
          var cy = s.top + s.height / 2 - box.top;
          var start = cx + s.width / 2 - 3;

          if (isDesktop) {
            // Horizontal from the hotspot, then angled up or down to the group tag
            var tag = this.querySelector('[data-tag="' + id + '"]');
            if (!tag) return;
            var t = tag.getBoundingClientRect();
            var endX = t.left - box.left - 12;
            var endY = t.top + t.height / 2 - box.top;
            var elbow = Math.max(start, endX - 28);
            paths.push('M' + start + ' ' + cy + 'H' + elbow + 'L' + endX + ' ' + endY);
          } else {
            // Mobile: tag beside the figure, level with its hotspot
            var label = this.querySelector('[data-tag-m="' + id + '"]');
            if (!label) return;
            var art = label.offsetParent ? label.offsetParent.getBoundingClientRect() : box;
            label.style.top = (s.top + s.height / 2 - art.top - label.offsetHeight / 2).toFixed(1) + 'px';
            label.classList.add('is-placed');
            var l = label.getBoundingClientRect();
            var lx = l.left - box.left - 8;
            if (lx > start) paths.push('M' + start + ' ' + cy + 'H' + lx);
          }
        });

        this.svg.setAttribute('viewBox', '0 0 ' + box.width.toFixed(1) + ' ' + box.height.toFixed(1));
        while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);
        paths.forEach((d) => {
          var path = document.createElementNS(SVG_NS, 'path');
          path.setAttribute('d', d);
          path.setAttribute('vector-effect', 'non-scaling-stroke');
          this.svg.appendChild(path);
        });
      }
    }

    customElements.define('sr-calc-map', SRCalcMap);
  }

  // "Find the right size": scroll to the anchor, or to the first Shift · Buy box
  if (!window.SRCalculatorCta) {
    window.SRCalculatorCta = true;
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-sr-calc-cta]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var target = null;
      try {
        target = href.length > 1 ? document.querySelector(href) : null;
      } catch (err) {
        target = null;
      }
      if (!target) {
        var bb = document.querySelector('sr-buy-box');
        target = bb ? bb.closest('section') || bb : null;
      }
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }
})();
